import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import { 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Loader2, 
  ArrowLeft,
  Calendar,
  User,
  Car,
  Image as ImageIcon,
  MapPin,
  DollarSign,
  Settings,
  FileText,
  Shield,
  Clock
} from 'lucide-react';
import vehicleService from '../../../services/vehicleService';
import vehicleOdometerService from '../../../services/vehicleOdometerService';
import rejectionReasonService from '../../../services/rejectionReasonService';
import { formatVehicleCategoriesFromDoc, formatCategoryLabel } from '../../../utils/vehicleCategoryDisplay';
import { ROUTES } from '../../../constants/routes.constants';
import { buildLatestChangeMap, formatDiffValue, isMongoIdString } from '../../../utils/vehicleDiff';
import DiffField, { VehicleDiffProvider } from '../../../components/admin/vehicles/DiffField';
import {
  buildRejectionMessageFromReason,
  DEFAULT_REJECTION_TEMPLATE,
  fillRejectionTemplate,
} from '../../../utils/rejectionTemplate';

// Helper function to convert relative URLs to absolute URLs
const getAbsoluteUrl = (url) => {
  if (!url || url.trim() === '') return url;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  if (url.startsWith('/')) {
    const apiBase = ROUTES.API_BASE_URL.replace('/api', '');
    return `${apiBase}${url}`;
  }
  return url;
};

// Fonction pour formater la date
const formatDate = (dateString) => {
  if (!dateString) return 'Date inconnue';
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const generateRejectionTemplate = (vehicle) => fillRejectionTemplate(DEFAULT_REJECTION_TEMPLATE, vehicle);

const VehicleReviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionMessage, setRejectionMessage] = useState('');
  const [rejectError, setRejectError] = useState('');
  const [history, setHistory] = useState([]);
  const [odometerRanges, setOdometerRanges] = useState([]);
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [selectedRejectionReasonId, setSelectedRejectionReasonId] = useState('');

  useEffect(() => {
    if (id) {
      loadVehicleData();
    }
  }, [id]);


  const loadVehicleData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await vehicleService.getVehicleById(id);
      
      if (response.success && response.data) {
        const vehicleData = response.data;
        setVehicle(vehicleData);
        try {
          const historyResponse = await vehicleService.getVehicleHistory(id, { limit: 30 });
          setHistory(historyResponse?.data || []);
        } catch {
          setHistory([]);
        }
        try {
          const odometerRes = await vehicleOdometerService.getAllOdometers({ limit: 200, status: '' });
          setOdometerRanges(odometerRes?.data || []);
        } catch {
          setOdometerRanges([]);
        }
      } else {
        setError('Véhicule non trouvé');
      }
    } catch (err) {
      console.error('Error loading vehicle:', err);
      setError('Erreur lors du chargement du véhicule: ' + (err.message || 'Erreur inconnue'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir approuver ce véhicule ? Il sera activé et visible pour les utilisateurs.')) {
      return;
    }

    try {
      setIsApproving(true);
      
      // Activer et vérifier le véhicule
      await vehicleService.toggleVerified(id);
      await vehicleService.toggleActive(id);
      
      // Réinitialiser le refus
      await vehicleService.resetVehicleRejection(id);
      
      alert('✅ Véhicule approuvé avec succès ! L\'agence a été notifiée.');
      navigate(ROUTES.ADMIN_VEHICLES_LIST);
    } catch (err) {
      console.error('Error approving vehicle:', err);
      alert('Erreur lors de l\'approbation: ' + (err.message || 'Erreur inconnue'));
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionMessage.trim()) {
      setRejectError('Veuillez renseigner le message de refus');
      return;
    }

    try {
      setIsRejecting(true);
      setRejectError('');
      
      await vehicleService.rejectVehicle(id, rejectionMessage.trim());
      
      alert('❌ Véhicule rejeté. L\'agence a été notifiée.');
      navigate(ROUTES.ADMIN_VEHICLES_LIST);
    } catch (err) {
      console.error('Error rejecting vehicle:', err);
      setRejectError('Erreur lors du rejet: ' + (err.message || 'Erreur inconnue'));
    } finally {
      setIsRejecting(false);
    }
  };

  const applyRejectionReason = (reasonId, reasonsList = rejectionReasons) => {
    setSelectedRejectionReasonId(reasonId);
    if (!reasonId) {
      setRejectionMessage(generateRejectionTemplate(vehicle));
      return;
    }
    const reason = reasonsList.find((item) => String(item._id) === String(reasonId));
    setRejectionMessage(buildRejectionMessageFromReason(reason, vehicle));
  };

  const openRejectModal = async () => {
    setShowRejectModal(true);
    setRejectError('');
    setSelectedRejectionReasonId('');
    setRejectionMessage(generateRejectionTemplate(vehicle));
    try {
      const response = await rejectionReasonService.getAll({
        limit: 200,
        status: 'active',
        sortBy: 'title',
        sortOrder: 'asc',
      });
      const list = response?.data || [];
      setRejectionReasons(list);
      if (list.length === 1) {
        applyRejectionReason(list[0]._id, list);
      }
    } catch (err) {
      console.error('Error loading rejection reasons:', err);
      setRejectionReasons([]);
    }
  };

  const odometerLabelById = useMemo(() => {
    const map = {};
    (odometerRanges || []).forEach((range) => {
      if (range?._id && range?.name) {
        map[String(range._id)] = range.name;
      }
    });
    const current = vehicle?.specs?.odometer;
    if (current && typeof current === 'object' && current._id && current.name) {
      map[String(current._id)] = current.name;
    }
    return map;
  }, [odometerRanges, vehicle]);

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-[#27489E] mx-auto mb-4" />
            <p className="text-gray-600">Chargement du véhicule...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !vehicle) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 mb-4">{error || 'Véhicule non trouvé'}</p>
            <button
              onClick={() => navigate(ROUTES.ADMIN_VEHICLES_LIST)}
              className="px-4 py-2 bg-[#27489E] text-white rounded-lg hover:bg-[#1e3a7d] transition"
            >
              Retour à la liste
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const rejectionReason = vehicle.rejectionReason;
  const brand = vehicle.specs?.brand?.name || 'Marque inconnue';
  const model = vehicle.specs?.model?.name || 'Modèle inconnu';
  const year = vehicle.specs?.year || 'Année inconnue';
  const adminName = 'Admin'; // Pourrait être amélioré en peuplant rejectedBy côté backend

  const resolveOdometerLabel = (odometer) => {
    if (odometer == null || odometer === '') return 'Non renseigné';
    if (typeof odometer === 'object') {
      return odometer.name || odometer.label || odometerLabelById[String(odometer._id || '')] || 'Non renseigné';
    }
    const asString = String(odometer);
    if (isMongoIdString(asString)) {
      return odometerLabelById[asString] || odometerLabelById[asString.toLowerCase()] || 'Non renseigné';
    }
    return asString;
  };

  const latestChangeMap = buildLatestChangeMap(history, vehicle.lastChanges, odometerLabelById);
  const hasPendingEdits = latestChangeMap.size > 0;

  const renderField = (label, value, fieldName = null, displayValue = null) => {
    const valueToDisplay = (v) => {
      if (v == null || v === '') return 'Non renseigné';
      if (typeof v === 'object') {
        return v.name || v.label || v.cityName || 'Non renseigné';
      }
      if (isMongoIdString(String(v))) {
        return odometerLabelById[String(v)] || 'Non renseigné';
      }
      return String(v);
    };
    const display = displayValue !== null ? displayValue : valueToDisplay(value);
    const content = <p className="font-medium text-gray-900">{display}</p>;
    if (!fieldName) {
      return (
        <div className="p-3 rounded-lg">
          <label className="text-sm font-medium text-gray-500">{label}</label>
          {content}
        </div>
      );
    }
    return (
      <DiffField fieldKey={fieldName} label={label} className="p-3 rounded-lg">
        {content}
      </DiffField>
    );
  };

  return (
    <DashboardLayout>
      <VehicleDiffProvider
        history={history}
        lastChanges={vehicle.lastChanges}
        currency={vehicle.pricing?.currency || 'MAD'}
        idLabels={odometerLabelById}
      >
      <div className="space-y-6 p-6">
        {/* Header avec bouton retour */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(ROUTES.ADMIN_VEHICLES_LIST)}
              className="p-2 text-gray-600 hover:text-[#27489E] hover:bg-gray-100 rounded-lg transition"
              title="Retour"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Révision du véhicule</h1>
              <p className="text-gray-500 mt-1">
                {brand} {model} ({year}) - {vehicle.licencePlateNumber || 'Sans matricule'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-900">Révision des modifications soumises par le vendeur</p>
            <p className="text-sm text-amber-800 mt-1">
              Les champs modifiés sont surlignés : ancienne valeur barrée en rouge, nouvelle valeur en vert.
            </p>
          </div>
        </div>

        {/* Boutons d'action en haut de la page */}
        {hasPendingEdits && (
          <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-orange-500">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Modifications en attente de validation</h3>
                <p className="text-sm text-gray-600">
                  Ce véhicule a été modifié. Veuillez examiner les changements et prendre une décision.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={openRejectModal}
                  disabled={isRejecting || isApproving}
                  className="px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-md"
                >
                  {isRejecting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Rejet...
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4" />
                      Rejeter les modifications
                    </>
                  )}
                </button>
                <button
                  onClick={handleApprove}
                  disabled={isRejecting || isApproving}
                  className="px-6 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-md"
                >
                  {isApproving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Approbation...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Approuver les modifications
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ZONE 1 : Le Rappel (Pourquoi j'avais dit non ?) */}
        {rejectionReason && (
          <div className="bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-500 rounded-lg p-6 shadow-md">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-8 h-8 text-red-600 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h2 className="text-xl font-bold text-red-900 mb-3 flex items-center gap-2">
                  ⚠️ Dernier Motif de Refus
                </h2>
                <div className="bg-white rounded-lg p-4 mb-4 border border-red-200">
                  <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">
                    {rejectionReason.message}
                  </p>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>Refusé le : <strong>{formatDate(rejectionReason.rejectedAt)}</strong></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span>Par : <strong>{adminName}</strong></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ZONE 2 : Les données actuelles du véhicule */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Car className="w-6 h-6 text-[#27489E]" />
            Données actuelles du véhicule
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Informations de base */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">Informations de base</h3>
              
              <div className="space-y-3">
                {renderField('Nom du véhicule', vehicle.title, 'title')}
                {renderField('Description', vehicle.description, 'description')}
                {renderField('Marque', brand, 'specs.brand', brand)}
                {renderField('Modèle', model, 'specs.model', model)}
                {renderField('Année', year, 'specs.year')}
                {renderField('Matricule', vehicle.licencePlateNumber, 'licencePlateNumber')}
                {renderField(
                  'Type',
                  vehicle.type ?? vehicle.vehicleType,
                  'vehicleType',
                  vehicle.type || formatCategoryLabel(vehicle.vehicleType) || 'Non renseigné'
                )}
                {renderField('Catégorie', vehicle.category, 'category', formatVehicleCategoriesFromDoc(vehicle))}
              </div>
            </div>

            {/* Spécifications techniques */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">Spécifications</h3>
              
              <div className="space-y-3">
                {renderField('Carburant', vehicle.specs?.fuel, 'specs.fuel', vehicle.specs?.fuel?.name || 'Non renseigné')}
                {renderField('Transmission', vehicle.specs?.transmission, 'specs.transmission')}
                {renderField('Places', vehicle.specs?.seats, 'specs.seats')}
                {renderField(
                  'Kilométrage',
                  vehicle.specs?.odometer,
                  'specs.odometer',
                  resolveOdometerLabel(vehicle.specs?.odometer)
                )}
              </div>
            </div>

            {/* Tarification */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-700 border-b pb-2 flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Tarification
              </h3>
              
              <div className="space-y-3">
                {renderField(
                  'Prix journalier',
                  `${vehicle.pricing?.basePrice?.toLocaleString('fr-FR') || '0'} ${vehicle.pricing?.currency || 'MAD'}/jour`,
                  'pricing.pricePerDay',
                  `${vehicle.pricing?.basePrice?.toLocaleString('fr-FR') || '0'} ${vehicle.pricing?.currency || 'MAD'}/jour`
                )}
                {vehicle.pricing?.weeklyDiscount && (
                  <DiffField fieldKey="pricing.weeklyDiscount" label="Réduction hebdomadaire" className="p-3 rounded-lg">
                    <p className="font-medium text-gray-900">
                      {vehicle.pricing.weeklyDiscount.value}
                      {vehicle.pricing.weeklyDiscount.type === 'PERCENTAGE' ? '%' : ` ${vehicle.pricing?.currency || 'MAD'}`}
                    </p>
                  </DiffField>
                )}
                {vehicle.pricing?.monthlyDiscount && (
                  <DiffField fieldKey="pricing.monthlyDiscount" label="Réduction mensuelle" className="p-3 rounded-lg">
                    <p className="font-medium text-gray-900">
                      {vehicle.pricing.monthlyDiscount.value}
                      {vehicle.pricing.monthlyDiscount.type === 'PERCENTAGE' ? '%' : ` ${vehicle.pricing?.currency || 'MAD'}`}
                    </p>
                  </DiffField>
                )}
                {vehicle.pricing?.homeDeliveryPrice && (
                  <DiffField
                    fieldKey="pricing.homeDeliveryPrice"
                    label="Livraison à domicile"
                    className="p-3 rounded-lg"
                    formatValue={(value) => formatDiffValue(value, { currency: vehicle.pricing?.currency || 'MAD' })}
                  >
                    <p className="font-medium text-gray-900">
                      {vehicle.pricing.homeDeliveryPrice?.toLocaleString('fr-FR') || '0'} {vehicle.pricing?.currency || 'MAD'}
                    </p>
                  </DiffField>
                )}
                {vehicle.pricing?.deposit && (
                  <DiffField
                    fieldKey="pricing.deposit"
                    label="Caution"
                    className="p-3 rounded-lg"
                    formatValue={(value) => formatDiffValue(value, { currency: vehicle.pricing?.currency || 'MAD' })}
                  >
                    <p className="font-medium text-gray-900">
                      {vehicle.pricing.deposit.value?.toLocaleString('fr-FR') || '0'} {vehicle.pricing.currency || 'MAD'}
                      <span className="ml-2 text-sm text-gray-500">
                        (Gérée par {vehicle.pricing.deposit.managedBy === 'CARVY' ? (
                          <span><span className="text-[#27489E]">Car</span><span className="text-orange-500">vy</span></span>
                        ) : (
                          vehicle.owner?.vendorDetails?.companyName || 'l\'agence'
                        )})
                      </span>
                    </p>
                  </DiffField>
                )}
              </div>
            </div>

            {/* Localisation */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-700 border-b pb-2 flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Localisation
              </h3>
              
              <div className="space-y-3">
                {renderField(
                  'Ville',
                  vehicle.location?.city || vehicle.vehicleLocation?.cityName,
                  'location.city',
                  vehicle.location?.city || vehicle.vehicleLocation?.cityName || 'Non renseigné'
                )}
                {renderField('Adresse', vehicle.location?.address, 'location.address')}
              </div>
            </div>
          </div>

          {/* Photos */}
          {vehicle.images && vehicle.images.length > 0 && (
            <DiffField fieldKey="images" className="mt-6 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-700 border-b pb-2 flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                Photos du véhicule
                <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                  {vehicle.images.length} photo{vehicle.images.length > 1 ? 's' : ''}
                </span>
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                {vehicle.images.map((image, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={getAbsoluteUrl(image)}
                      alt={`Véhicule ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg border border-gray-200"
                    />
                  </div>
                ))}
              </div>
            </DiffField>
          )}

          {/* Équipements */}
          {vehicle.features && vehicle.features.length > 0 && (
            <DiffField fieldKey="features" className="mt-6 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Équipements
                </h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {vehicle.features.map((feature) => (
                  <span
                    key={feature._id || feature}
                    className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
                  >
                    {typeof feature === 'object' ? feature.name : feature}
                  </span>
                ))}
              </div>
            </DiffField>
          )}

          {/* Règles du véhicule */}
          {vehicle.rules && vehicle.rules.length > 0 && (
            <DiffField fieldKey="rules" className="mt-6 space-y-4 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Règles du véhicule
              </h3>
              <div className="flex flex-wrap gap-2">
                {vehicle.rules.map((rule) => (
                  <span
                    key={rule._id || rule}
                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                  >
                    {typeof rule === 'object' ? rule.name : rule}
                  </span>
                ))}
              </div>
            </DiffField>
          )}

          {/* Politiques d'annulation */}
          {vehicle.cancellationPolicies && vehicle.cancellationPolicies.length > 0 && (
            <DiffField fieldKey="cancellationPolicies" className="mt-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">Politiques d'annulation</h3>
              <div className="space-y-2">
                {vehicle.cancellationPolicies.map((policy, index) => {
                  let description = typeof policy.policy === 'object' && policy.policy.description 
                    ? policy.policy.description 
                    : '';
                  
                  if (description) {
                    description = description.replace(/x%/gi, `${policy.percentage}%`);
                    description = description.replace(/\bx\b/gi, `${policy.percentage}`);
                  }
                  
                  return (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg">
                      <div className="font-medium text-gray-900">
                        {typeof policy.policy === 'object' ? policy.policy.name : policy.policy}
                      </div>
                      {description && (
                        <p className="text-sm text-gray-600 mt-1">{description}</p>
                      )}
                      <div className="text-sm text-gray-500 mt-1">
                        Pourcentage personnalisé: {policy.percentage}%
                      </div>
                    </div>
                  );
                })}
              </div>
            </DiffField>
          )}

          {/* Règles de location */}
          <div className="mt-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">Règles de location</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderField('Jours minimum', vehicle.minRentalDays, 'minRentalDays')}
              {renderField('Couverture d\'assurance', vehicle.insuranceCoverage, 'insuranceCoverage')}
              {renderField(
                'Âge minimum',
                vehicle.ageRestriction?.enabled ? `${vehicle.ageRestriction.minimumAge} ans` : 'Non',
                'ageRestriction.minimumAge',
                vehicle.ageRestriction?.enabled ? `${vehicle.ageRestriction.minimumAge} ans` : 'Non'
              )}
              {renderField('Fumer autorisé', vehicle.smokingAllowed, 'smokingAllowed', vehicle.smokingAllowed ? 'Oui' : 'Non')}
              {renderField('Voyage international', vehicle.internationalTravelAllowed, 'internationalTravelAllowed', vehicle.internationalTravelAllowed ? 'Oui' : 'Non')}
            </div>
          </div>

          {/* Documents administratifs */}
          <div className="mt-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-700 border-b pb-2 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Documents administratifs
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {vehicle.registrationCardFront && (
                <div>
                  <p className="text-sm text-gray-500 mb-2 font-medium">Carte grise (Recto)</p>
                  <a
                    href={getAbsoluteUrl(vehicle.registrationCardFront)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-4 bg-blue-50 rounded-lg text-center hover:bg-blue-100 transition border-2 border-blue-200"
                  >
                    <FileText className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                    <p className="text-xs font-medium text-gray-700">Voir le document</p>
                  </a>
                </div>
              )}
              {vehicle.registrationCardBack && (
                <div>
                  <p className="text-sm text-gray-500 mb-2 font-medium">Carte grise (Verso)</p>
                  <a
                    href={getAbsoluteUrl(vehicle.registrationCardBack)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-4 bg-blue-50 rounded-lg text-center hover:bg-blue-100 transition border-2 border-blue-200"
                  >
                    <FileText className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                    <p className="text-xs font-medium text-gray-700">Voir le document</p>
                  </a>
                </div>
              )}
              {vehicle.ministryAuthorization && (
                <div>
                  <p className="text-sm text-gray-500 mb-2 font-medium">Autorisation ministère</p>
                  <a
                    href={getAbsoluteUrl(vehicle.ministryAuthorization)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-4 bg-blue-50 rounded-lg text-center hover:bg-blue-100 transition border-2 border-blue-200"
                  >
                    <FileText className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                    <p className="text-xs font-medium text-gray-700">Voir le document</p>
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Statuts */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Statuts
            </h3>
            <div className="flex flex-wrap gap-3">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                vehicle.isActive 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {vehicle.isActive ? '✓ Actif' : '✗ Inactif'}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                vehicle.isVerified 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {vehicle.isVerified ? '✓ Vérifié' : '✗ Non vérifié'}
              </span>
              {vehicle.isFeatured && (
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  ⭐ En vedette
                </span>
              )}
            </div>
          </div>

          {history.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Historique des révisions
              </h3>
              <div className="space-y-3">
                {history.map((revision) => (
                  <div key={revision._id} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <p className="text-xs text-gray-500 mb-2">
                      {formatDate(revision.createdAt)}
                      {' · '}
                      {revision.user?.companyName || revision.user?.fullName || revision.user?.email || 'Utilisateur'}
                      {revision.userRole ? ` (${revision.userRole})` : ''}
                    </p>
                    <ul className="space-y-1">
                      {(revision.changes || []).map((change, index) => (
                        <li key={`${revision._id}-${change.field}-${index}`} className="text-sm text-gray-800">
                          <span className="font-medium">{change.label || change.field}</span>
                          {': '}
                          <span className="line-through text-red-600">{formatDiffValue(change.oldValue, { idLabels: odometerLabelById })}</span>
                          {' → '}
                          <span className="text-green-600 font-bold">{formatDiffValue(change.newValue, { idLabels: odometerLabelById })}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ZONE 3 : Actions Immédiates */}
        <div className="bg-white rounded-lg shadow-md p-6 sticky bottom-0 z-10 border-t-4 border-[#27489E]">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Décision</h3>
              <p className="text-sm text-gray-600">
                Après avoir examiné les modifications, prenez une décision finale
              </p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={openRejectModal}
                disabled={isRejecting || isApproving}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg"
              >
                {isRejecting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Rejet en cours...
                  </>
                ) : (
                  <>
                    <XCircle className="w-5 h-5" />
                    ❌ Rejeter encore
                  </>
                )}
              </button>
              <button
                onClick={handleApprove}
                disabled={isRejecting || isApproving}
                className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg"
              >
                {isApproving ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Approbation...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    ✅ Approuver maintenant
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Modal de rejet */}
        {showRejectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <XCircle className="w-6 h-6 text-red-600" />
                    Rejeter le véhicule
                  </h3>
                  <button
                    onClick={() => setShowRejectModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 flex-1 overflow-y-auto">
                <p className="text-gray-700 mb-4">
                  Choisissez un motif : le message se remplit automatiquement (marque, modèle, plaque). Vous pouvez encore l’ajuster avant confirmation.
                </p>
                
                {rejectError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{rejectError}</p>
                  </div>
                )}

                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Motif de rejet
                </label>
                <select
                  value={selectedRejectionReasonId}
                  onChange={(e) => applyRejectionReason(e.target.value)}
                  className="w-full mb-4 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
                >
                  <option value="">Modèle par défaut</option>
                  {rejectionReasons.map((reason) => (
                    <option key={reason._id} value={reason._id}>
                      {reason.title}
                    </option>
                  ))}
                </select>
                {rejectionReasons.length === 0 && (
                  <p className="mb-4 text-xs text-gray-500">
                    Aucun motif actif. Ajoutez-en dans Véhicules → Motifs de rejet.
                  </p>
                )}
                
                <textarea
                  value={rejectionMessage}
                  onChange={(e) => setRejectionMessage(e.target.value)}
                  className="w-full h-64 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 font-mono text-sm"
                  placeholder="Motif de refus..."
                />
              </div>
              
              <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowRejectModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                  disabled={isRejecting}
                >
                  Annuler
                </button>
                <button
                  onClick={handleReject}
                  disabled={isRejecting || !rejectionMessage.trim()}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isRejecting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Envoi...
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4" />
                      Confirmer le rejet
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      </VehicleDiffProvider>
    </DashboardLayout>
  );
};

export default VehicleReviewPage;

